/**
 * PTV2-1327 — suppress the green success toast for autosave-driven saves.
 *
 * With autosave on (Google-docs mode) a card commits every few seconds; a toast on each would spam the
 * corner. The "Saved" status indicator conveys it instead. The primitive wraps its AUTOSAVE commit in
 * {@link suppressSuccessToastDuring}; the global `MutationCache.onSuccess` (query/client.ts) skips the
 * toast while {@link isSuccessToastSuppressed} is true. Explicit Save (autosave off) is NOT wrapped, so
 * a manual save still confirms.
 *
 * A depth counter (not a boolean) so overlapping autosaves nest correctly.
 */
let depth = 0;

export function isSuccessToastSuppressed(): boolean {
  return depth > 0;
}

/**
 * Run `commit` with success toasts suppressed until it settles. The suppression spans the mutation's
 * lifecycle: `MutationCache.onSuccess` fires while the returned promise is still settling, so the flag
 * is up when the toast would be shown.
 */
export function suppressSuccessToastDuring<T>(commit: () => T | Promise<T>): Promise<T> {
  depth += 1;
  let result: T | Promise<T>;
  try {
    // Call SYNCHRONOUSLY — the mutation must fire on this tick (deferring it to a microtask would
    // change save timing). Suppression stays up until the returned promise settles.
    result = commit();
  } catch (error) {
    depth = Math.max(0, depth - 1);
    throw error;
  }
  return Promise.resolve(result).finally(() => {
    depth = Math.max(0, depth - 1);
  });
}
